// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Filters section with left orange accent stripe
// - Inputs/autocomplete: soft blue border + orange focus
// - Buttons: primary orange, outlined blue
// - Tables: blue header, soft zebra rows, hover tint, subtle borders
// - Status pill styling (Pending/Approved/Rejected) to fit theme
// - No API/business logic changed

import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Autocomplete,
  LinearProgress,
  CircularProgress,
  Typography,
  Box,
  Divider,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { API_URL, API_UPLOAD_URL } from "./config";
import { formatDateTimeIST } from "./commonfunctions/dateUtils";

const ReportTimeOffRequest = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [studentList, setStudentList] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [shouldSearch, setShouldSearch] = useState(false);
  const [logincampusCode, setCampusCode] = useState("");
  const token = localStorage.getItem("auth");

  // dropdown filters and options
  const [filters, setFilters] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });

  const [options, setOptions] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });

  const [loadingLookups, setLoadingLookups] = useState(false);

  // students list based on dropdown filters
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [allFilteredStudents, setAllFilteredStudents] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(false);

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const tableWrapSx = {
    ...surfaceSx,
    overflow: "hidden",
    overflowX: "auto",     // ✅ allow left-right scroll
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch", // ✅ smooth iOS scrolling
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const statusPillSx = (status) => {
    const base = {
      display: "inline-block",
      px: 1.2,
      py: 0.4,
      borderRadius: 1.5,
      fontWeight: 900,
      fontSize: "0.78rem",
      lineHeight: 1,
      border: "1px solid rgba(0,0,0,0.08)",
      whiteSpace: "nowrap",
    };

    if (status === "Pending") return { ...base, color: "#6a4b00", backgroundColor: "rgba(255,193,7,0.35)" };
    if (status === "Rejected") return { ...base, color: "white", backgroundColor: "rgba(244,67,54,0.85)" };
    if (status === "Approved") return { ...base, color: "white", backgroundColor: "rgba(76,175,80,0.85)" };
    return { ...base, color: "rgba(25,118,210,0.95)", backgroundColor: "rgba(25,118,210,0.06)" };
  };

  const getUploadUrl = (value) => {
    if (!value) return "";
    if (value.startsWith("blob:") || value.startsWith("http")) return value;
    return `${API_UPLOAD_URL}/uploads/${value}`;
  };

  const AccompanyingPhotoThumb = ({ src, label = "Photo" }) => {
    const imgSrc = getUploadUrl(src);

    if (!imgSrc) return <Typography sx={{ color: "text.secondary" }}>-</Typography>;

    return (
      <Tooltip
        arrow
        placement="right"
        title={
          <Box
            component="img"
            src={imgSrc}
            alt={label}
            sx={{
              width: 160,
              height: 190,
              objectFit: "cover",
              borderRadius: 1,
            }}
          />
        }
      >
        <Box
          component="img"
          src={imgSrc}
          alt={label}
          sx={{
            width: 34,
            height: 40,
            objectFit: "cover",
            borderRadius: 1,
            border: `1px solid ${BORDER}`,
            cursor: "pointer",
            backgroundColor: "rgba(25,118,210,0.04)",
          }}
        />
      </Tooltip>
    );
  };

  // Extract CampusCode from the JWT
  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        const campusCode = decodedToken.campusCode || decodedToken.CampusCode;
        setCampusCode(campusCode || "");
      } catch (err) {
        console.error("Error decoding token:", err);
        setCampusCode("");
      }
    }
  }, [token]);

  // fetch dropdown lookups
  useEffect(() => {
    if (!logincampusCode) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLookups(true);
        const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
          ...authHeader,
          params: { CampusCode: logincampusCode },
        });

        if (cancelled) return;
        setOptions({
          SubCampusCode: data?.SubCampusCode || [],
          DegreeCode: data?.DegreeCode || [],
          BranchCode: data?.BranchCode || [],
          SemesterCode: (data?.SemesterCode || []).map(String),
        });
      } catch (e) {
        console.error("Failed to load dropdowns:", e);
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [logincampusCode, authHeader]);

  // Fetch student list for dropdown (free-text search)
  useEffect(() => {
    const fetchStudentList = async () => {
      if (searchTerm.length < 3 || !logincampusCode) {
        setStudentList([]);
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/api/studentList?search=${encodeURIComponent(searchTerm)}&CampusCode=${encodeURIComponent(
            logincampusCode
          )}`,
          { ...authHeader }
        );
        setStudentList(response.data || []);
      } catch (error) {
        console.error("Error fetching student list:", error);
      }
    };

    const t = setTimeout(fetchStudentList, 250);
    return () => clearTimeout(t);
  }, [searchTerm, logincampusCode, authHeader]);

  // Fetch time-off requests for selected student
  useEffect(() => {
    const fetchData = async () => {
      if (!shouldSearch || !selectedStudent || !logincampusCode) {
        setData([]);
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/api/dataTimeOffRequestManage?search=${encodeURIComponent(
            selectedStudent.StudentID
          )}&CampusCode=${encodeURIComponent(logincampusCode)}`,
          { ...authHeader }
        );
        setData(response.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        if (error.response && error.response.status === 401) navigate("/");
      }
    };

    fetchData();
  }, [shouldSearch, selectedStudent, logincampusCode, navigate, authHeader]);

  const handleSearch = () => {
    if (!selectedStudent) {
      setData([]);
      setShouldSearch(false);
    } else {
      setShouldSearch(true);
    }
  };

  // filter students using dropdowns
  const anyFilterSelected =
    (filters.SubCampusCode?.length || 0) > 0 ||
    (filters.DegreeCode?.length || 0) > 0 ||
    (filters.BranchCode?.length || 0) > 0 ||
    (filters.SemesterCode?.length || 0) > 0;

  const handleFilterStudents = async () => {
    setListError("");
    setFilteredStudents([]);
    setAllFilteredStudents([]);
    setIsCollapsed(false);

    if (!logincampusCode) return;
    if (!anyFilterSelected) {
      setListError("Select at least one dropdown filter to load students.");
      return;
    }

    try {
      setLoadingList(true);
      const params = { CampusCode: logincampusCode };
      if (filters.SubCampusCode.length) params.SubCampusCode = filters.SubCampusCode;
      if (filters.DegreeCode.length) params.DegreeCode = filters.DegreeCode;
      if (filters.BranchCode.length) params.BranchCode = filters.BranchCode;
      if (filters.SemesterCode.length) params.SemesterCode = filters.SemesterCode;

      const { data } = await axios.get(`${API_URL}/api/dataReportStudentPersonalInformationList`, {
        ...authHeader,
        params,
      });

      const arr = Array.isArray(data) ? data : [];
      setFilteredStudents(arr);
      setAllFilteredStudents(arr);

      if (!arr.length) setListError("No students found for the selected filters.");
    } catch (e) {
      console.error("Error loading filtered students:", e);
      setListError("Failed to load students. Please try again.");
    } finally {
      setLoadingList(false);
    }
  };

  // collapse/expand behavior on student row click
  const handleStudentRowClick = (s) => {
    if (isCollapsed && filteredStudents.length === 1 && filteredStudents[0].StudentID === s.StudentID) {
      setFilteredStudents(allFilteredStudents);
      setIsCollapsed(false);
      return;
    }

    setFilteredStudents([s]);
    setIsCollapsed(true);

    setSelectedStudent(s);
    setShouldSearch(true);
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Time Off Request Report
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Filter students and view their time-off requests
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* ✅ Filters section */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Filters
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(5, 1fr)" },
                  gap: 1,
                  textAlign: "left",
                }}
              >
                <Autocomplete
                  multiple
                  options={options.SubCampusCode}
                  value={filters.SubCampusCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SubCampusCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SubCampusCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.DegreeCode}
                  value={filters.DegreeCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, DegreeCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="DegreeCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.BranchCode}
                  value={filters.BranchCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, BranchCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="BranchCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.SemesterCode}
                  value={filters.SemesterCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SemesterCode: value.map(String) }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SemesterCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Button
                    variant="contained"
                    onClick={handleFilterStudents}
                    disabled={loadingList}
                    sx={{ ...primaryBtnSx, height: 36, fontSize: "0.8rem" }}
                  >
                    {loadingList ? "Loading…" : "Filter Students"}
                  </Button>
                  {listError ? (
                    <Typography variant="body2" color="error">
                      {listError}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Box>
          </Paper>

          {loadingList ? (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
            </Box>
          ) : null}

          {/* ✅ Search section */}
          <Paper variant="outlined" sx={{ ...surfaceSx, p: 2, mt: 2 }}>
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
              Search
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Autocomplete
                freeSolo
                options={studentList}
                getOptionLabel={(option) =>
                  option?.FullName ? `${option.FullName} (${option.StudentID})` : ""
                }
                onInputChange={(_, value) => setSearchTerm(value)}
                onChange={(_, value) => setSelectedStudent(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Search by Name or ID" size="small" fullWidth sx={inputSx} />
                )}
                sx={{ flex: 1 }}
              />

              <Button variant="contained" onClick={handleSearch} sx={{ ...primaryBtnSx, height: 36, minWidth: 110 }}>
                Search
              </Button>

              <Button
                variant="outlined"
                sx={{ ...outlinedBtnSx, height: 36, minWidth: 110 }}
                onClick={() => {
                  setSelectedStudent(null);
                  setSearchTerm("");
                  setShouldSearch(false);
                  setData([]);
                }}
              >
                Clear
              </Button>
            </Box>
          </Paper>

          {/* ✅ Students list table (from filters) */}
          {filteredStudents.length > 0 ? (
            <Paper variant="outlined" sx={{ ...surfaceSx, mt: 2 }}>
              <Box sx={{ p: 1.5 }}>
                <Typography sx={{ mb: 1, fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                  Students Matching Filters {isCollapsed ? "(collapsed)" : ""}
                </Typography>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={theadRowSx}>
                        <TableCell>#</TableCell>
                        <TableCell>StudentID</TableCell>
                        <TableCell>Full Name</TableCell>
                        <TableCell>SubCampus</TableCell>
                        <TableCell>Degree</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell>Semester</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredStudents.map((s, idx) => (
                        <TableRow
                          key={`${s.StudentID}-${idx}`}
                          hover
                          sx={{
                            cursor: "pointer",
                            ...zebraRowSx(idx),
                          }}
                          onClick={() => handleStudentRowClick(s)}
                        >
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{s.StudentID}</TableCell>
                          <TableCell>{s.FullName}</TableCell>
                          <TableCell>{s.SubCampusCode}</TableCell>
                          <TableCell>{s.DegreeCode}</TableCell>
                          <TableCell>{s.BranchCode}</TableCell>
                          <TableCell>{s.SemesterCode}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          ) : null}

          {/* ✅ Time Off Table */}
          <TableContainer component={Paper} sx={{ ...tableWrapSx, mt: 2 }}>
            {!shouldSearch ? (
              <Box sx={{ p: 2 }}>
                <Typography align="center" sx={{ color: "rgba(25,118,210,0.80)", fontWeight: 700 }}>
                  Search a student to view time-off requests.
                </Typography>
              </Box>
            ) : data.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography align="center" sx={{ color: "rgba(25,118,210,0.80)", fontWeight: 700 }}>
                  No results found
                </Typography>
              </Box>
            ) : (
              <Table size="small" sx={{ width: "100%" }}>
                <TableHead>
                  <TableRow sx={theadRowSx}>
                    <TableCell>#</TableCell>
                    <TableCell>Semester</TableCell>
                    <TableCell>Leave Type</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Approved Start</TableCell>
                    <TableCell>Approved End</TableCell>
                    <TableCell>Days</TableCell>
                    <TableCell>OUT Photo</TableCell>
                    <TableCell>IN Photo</TableCell>
                    <TableCell>Comments</TableCell>
                    <TableCell>Approved By</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Approved Date</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {data.map((item, index) => (
                    <TableRow key={item.TimeOffRequestID} sx={zebraRowSx(index)}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{item.Semester}</TableCell>
                      <TableCell>{item.LeaveType}</TableCell>
                      <TableCell sx={{ maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {item.ReasonForLeave}
                      </TableCell>
                      <TableCell>{formatDateTimeIST(item.ApprovedLeaveStartDate)}</TableCell>
                      <TableCell>{formatDateTimeIST(item.ApprovedLeaveEndDate)}</TableCell>
                      <TableCell>{item.LeaveDays}</TableCell>
                      <TableCell>
                        <AccompanyingPhotoThumb
                          src={item.AccompanyingPersonPhoto_OUT}
                          label="OUT accompanying person"
                        />
                      </TableCell>
                      <TableCell>
                        <AccompanyingPhotoThumb
                          src={item.AccompanyingPersonPhoto_IN}
                          label="IN accompanying person"
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {item.Comments}
                      </TableCell>
                      <TableCell>{item.ApprovedBy}</TableCell>
                      <TableCell>
                        <Box component="span" sx={statusPillSx(item.LeaveStatus)}>
                          {item.LeaveStatus}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDateTimeIST(item.ApprovedDate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Box>
      </Paper>
    </Box>
  );
};

export default ReportTimeOffRequest;
